'use client';
import React from 'react';

// ─── Base Skeleton pulse ───────────────────────────────────────────────────────

function SkeletonBase({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

// ─── KPI / Stat Card Skeleton ─────────────────────────────────────────────────

export function SkeletonCard() {
  return (
    <div className="bg-card rounded-xl p-4 card-shadow border border-border">
      <SkeletonBase className="h-3 w-24 mb-3" />
      <SkeletonBase className="h-8 w-20 mb-2" />
      <SkeletonBase className="h-3 w-32" />
    </div>
  );
}

// ─── Table Row Skeleton ───────────────────────────────────────────────────────

export function SkeletonTableRow({ cols = 5 }: { cols?: number }) {
  const widths = ['w-32', 'w-24', 'w-20', 'w-28', 'w-16', 'w-20', 'w-24', 'w-16'];
  return (
    <tr className="border-b border-border">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <SkeletonBase className={`h-4 ${widths[i % widths.length]}`} />
        </td>
      ))}
    </tr>
  );
}

// ─── Table Skeleton (header + rows) ──────────────────────────────────────────

export function SkeletonTable({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <SkeletonBase className="h-3 w-16" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Chart Skeleton ───────────────────────────────────────────────────────────

export function SkeletonChart({ height = 'h-48' }: { height?: string }) {
  return (
    <div className={`w-full ${height} flex items-end gap-2 px-4 pb-4`} aria-hidden="true">
      {[60, 80, 45, 90, 70, 55, 85, 65, 75, 50].map((h, i) => (
        <div
          key={i}
          className="flex-1 bg-gray-200 rounded-t animate-pulse"
          style={{ height: `${h}%`, animationDelay: `${i * 60}ms` }}
        />
      ))}
    </div>
  );
}

// ─── Dashboard Skeleton (KPI cards + chart + table) ───────────────────────────

export function SkeletonDashboard({ cards = 4, tableRows = 5, tableCols = 5 }: { cards?: number; tableRows?: number; tableCols?: number }) {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* KPI Cards */}
      <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(cards, 4)} gap-4`}>
        {Array.from({ length: cards }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
      {/* Chart area */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl card-shadow border border-border p-5">
          <SkeletonBase className="h-4 w-48 mb-2" />
          <SkeletonBase className="h-3 w-32 mb-4" />
          <SkeletonChart height="h-40" />
        </div>
        <div className="bg-card rounded-2xl card-shadow border border-border p-5">
          <SkeletonBase className="h-4 w-40 mb-2" />
          <SkeletonBase className="h-3 w-28 mb-4" />
          <SkeletonChart height="h-40" />
        </div>
      </div>
      {/* Table */}
      <div className="bg-card rounded-2xl card-shadow border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <SkeletonBase className="h-4 w-40" />
        </div>
        <SkeletonTable rows={tableRows} cols={tableCols} />
      </div>
    </div>
  );
}

// ─── List Item Skeleton ───────────────────────────────────────────────────────

export function SkeletonListItem() {
  return (
    <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
      <SkeletonBase className="w-9 h-9 rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonBase className="h-3.5 w-40" />
        <SkeletonBase className="h-3 w-56" />
      </div>
      <SkeletonBase className="h-7 w-20 rounded-lg" />
    </div>
  );
}

// ─── Inline Button Spinner ────────────────────────────────────────────────────

export function ButtonSpinner({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className || 'w-4 h-4'}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export default SkeletonBase;
